// import _ from "lodash"; // Removed unused import
import JSZip from "jszip";
import saveAs from "file-saver";

const removeEmptyKeys = (data, exclude = []) => {
  const entries = Object.entries(data).filter(
    ([key, value]) =>
      !exclude.includes(key) &&
      value !== null &&
      value !== undefined &&
      value !== ""
  );

  const clean = entries?.map(([key, v]) => {
    const value =
      typeof v == "object" && !Array.isArray(v) ? removeEmptyKeys(v) : v;
    return [key, value];
  });

  return Object.fromEntries(clean);
};

const removeNullishKeys = (data, exclude = []) => {
  const entries = Object.entries(data).filter(
    ([key, value]) => !exclude.includes(key) && value
  );

  const clean = entries?.map(([key, v]) => {
    const value = typeof v == "object" ? removeEmptyKeys(v) : v;
    return [key, value];
  });

  return Object.fromEntries(clean);
};

export { removeEmptyKeys, removeNullishKeys };

export const isHttpValid = (str) => {
  try {
    const newUrl = new URL(str);
    return newUrl.protocol === "http:" || newUrl.protocol === "https:";
  } catch (err) {
    return false;
  }
};

export const downloadImagesAsZip = async (imageUrls, title) => {
  try {
    const zip = new JSZip();

    console.log(`Starting download of ${imageUrls.length} images...`);

    // Fetch and add each image with better error handling
    const imagePromises = imageUrls.map(async (url, index) => {
      try {
        // Try with CORS mode first
        const response = await fetch(url, {
          mode: 'cors',
          credentials: 'omit',
          headers: {
            'Accept': 'image/*',
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const blob = await response.blob();
        const fileName = url.split('/').pop() || `image_${index + 1}.jpg`;
        // Add files directly to zip root (no subfolder)
        zip.file(fileName, blob);
        console.log(`✅ Downloaded: ${fileName}`);
        return { success: true, fileName };
      } catch (error) {
        console.error(`❌ Failed to download image ${index + 1}:`, error);

        // Fallback: Try loading image through img element and convert to blob
        try {
          const blob = await loadImageAsBlob(url);
          const fileName = url.split('/').pop() || `image_${index + 1}.jpg`;
          // Add files directly to zip root (no subfolder)
          zip.file(fileName, blob);
          console.log(`✅ Downloaded (fallback): ${fileName}`);
          return { success: true, fileName };
        } catch (fallbackError) {
          console.error(`❌ Fallback also failed for image ${index + 1}:`, fallbackError);
          return { success: false, fileName: `image_${index + 1}`, error: fallbackError };
        }
      }
    });

    const results = await Promise.all(imagePromises);
    const successCount = results.filter(r => r.success).length;
    const failedCount = results.filter(r => !r.success).length;

    if (successCount === 0) {
      throw new Error('Failed to download any images. Please check CORS settings or try downloading images individually.');
    }

    // Generate zip and download
    console.log('Generating ZIP file...');
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    saveAs(zipBlob, `${title}.zip`);

    console.log(`✅ ${title} downloaded successfully. ${successCount} images included${failedCount > 0 ? `, ${failedCount} failed` : ''}.`);

    if (failedCount > 0) {
      alert(`Downloaded ${successCount} out of ${imageUrls.length} images. ${failedCount} images failed due to CORS restrictions.`);
    }
  } catch (error) {
    console.error('❌ Error creating ZIP:', error);
    alert(`Error creating ZIP file: ${error.message}. Please try again or contact support.`);
  }
};

// Helper function to load image as blob using canvas (bypasses some CORS issues)
const loadImageAsBlob = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);

        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to convert image to blob'));
          }
        }, 'image/jpeg', 0.95);
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error(`Failed to load image: ${url}`));
    };

    // Add timestamp to bypass cache and potentially help with CORS
    const separator = url.includes('?') ? '&' : '?';
    img.src = `${url}${separator}_t=${Date.now()}`;
  });
};